import React, { useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';
// Error Boundary Component with app styling
const ErrorBoundary = ({ error, resetError }) => {
  const { theme } = useAppTheme();

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: theme.colors.background.main,
      color: theme.colors.text.primary,
      fontFamily: theme.fontFamily.primary,
      padding: theme.spacing.lg,
    },
    errorCard: {
      backgroundColor: theme.colors.background.modal,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      maxWidth: '600px',
      width: '100%',
      border: `1px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.modal,
    },
    errorIcon: {
      fontSize: '48px',
      marginBottom: theme.spacing.md,
      color: theme.colors.error.text,
    },
    errorTitle: {
      fontSize: theme.fontSize.title,
      fontWeight: theme.fontWeight.semibold,
      marginBottom: theme.spacing.sm,
      color: theme.colors.text.primary,
    },
    errorSubtitle: {
      fontSize: theme.fontSize.xl,
      color: theme.colors.text.secondary,
      marginBottom: theme.spacing.lg,
      lineHeight: '1.5',
    },
    errorMessage: {
      backgroundColor: theme.colors.background.sidebar,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.md,
      marginBottom: theme.spacing.lg,
      border: `1px solid ${theme.colors.border}`,
      fontFamily: theme.fontFamily.monospace,
      fontSize: theme.fontSize.md,
      color: theme.colors.error.text,
      overflow: 'auto',
      maxHeight: '200px',
    },
    buttonContainer: {
      display: 'flex',
      gap: theme.spacing.sm,
      justifyContent: 'center',
    },
    button: {
      backgroundColor: theme.colors.background.button,
      color: theme.colors.text.primary,
      border: 'none',
      borderRadius: theme.borderRadius.md,
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      fontSize: theme.fontSize.md,
      fontWeight: theme.fontWeight.medium,
      cursor: 'pointer',
      transition: theme.transitions.fast,
    },
    buttonSecondary: {
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
      border: `1px solid ${theme.colors.border}`,
    },
    buttonHover: {
      backgroundColor: theme.colors.background_hover.button,
    },
    buttonSecondaryHover: {
      backgroundColor: theme.colors.background.sidebar,
      color: theme.colors.text.primary,
    },
    helpText: {
      fontSize: theme.fontSize.md,
      color: theme.colors.text.muted,
      marginTop: theme.spacing.lg,
      textAlign: 'center',
      lineHeight: '1.4',
    },
  };

  const handleRetry = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div style={styles.container}>
      <div style={styles.errorCard}>
        <div style={styles.errorIcon}>⚠️</div>
        <h1 style={styles.errorTitle}>Unexpected Application Error!</h1>
        <p style={styles.errorSubtitle}>
          Something went wrong while loading this page. Don't worry, this has been logged and we'll look into it.
        </p>

        {error && (
          <div style={styles.errorMessage}>
            <strong>Error Details:</strong>
            <br />
            {error.message || 'An unknown error occurred'}
          </div>
        )}

        <div style={styles.buttonContainer}>
          <button
            style={styles.button}
            onClick={handleRetry}
            onMouseEnter={(e) => Object.assign(e.target.style, styles.buttonHover)}
            onMouseLeave={(e) => Object.assign(e.target.style, styles.button)}
          >
            Try Again
          </button>
          <button
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={handleGoHome}
            onMouseEnter={(e) => Object.assign(e.target.style, styles.buttonSecondaryHover)}
            onMouseLeave={(e) => Object.assign(e.target.style, { ...styles.button, ...styles.buttonSecondary })}
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
